// src/components/artist/ArtistSubscriptionModal.jsx

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, AlertCircle, Zap, ArrowRight, ChevronLeft } from 'lucide-react';
import {
  SUBSCRIPTION_TIERS,
  SUBSCRIPTION_DETAILS
} from './constants/subscriptionConfig';

export default function ArtistSubscriptionModal({
  isOpen,
  onClose,
  onSelectSubscription,
  isProcessing,
  currentUser
}) {
  const [step, setStep] = useState('info');
  const [selectedTier, setSelectedTier] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const [artistInfo, setArtistInfo] = useState({
    name: currentUser?.displayName || '',
    email: currentUser?.email || '',
    type: 'solo',
    genre: '',
    bio: '',
    website: '',
    socialMedia: {
      instagram: '',
      twitter: '',
      youtube: '',
      facebook: '',
    }
  });

  const [formErrors, setFormErrors] = useState({});

  const tiers = [
    SUBSCRIPTION_TIERS.STARTER,
    SUBSCRIPTION_TIERS.PRO,
    SUBSCRIPTION_TIERS.ENTERPRISE
  ];

  const validateForm = () => {
    const errors = {};
    
    if (!artistInfo.name.trim()) errors.name = 'Artist/Band name is required';
    if (!artistInfo.email.trim()) errors.email = 'Email is required';
    if (!/^\S+@\S+\.\S+$/.test(artistInfo.email)) errors.email = 'Valid email is required';
    if (!artistInfo.type) errors.type = 'Please select artist type';
    if (!artistInfo.genre.trim()) errors.genre = 'Genre is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (validateForm()) {
      setStep('tier');
    }
  };

  const handleSelectAndProceed = (tier) => {
    setSelectedTier(tier);
    if (agreedToTerms) {
      onSelectSubscription(tier, artistInfo);
    }
  };

  const handleBack = () => {
    setStep('info');
    setSelectedTier(null);
    setAgreedToTerms(false);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto font-sans antialiased"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-[#121318] border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl my-auto text-white relative overflow-hidden flex flex-col max-h-[90vh]"
          onClick={e => e.stopPropagation()}
        >
          {/* Ambient Glow */}
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#bd9f67]/15 rounded-full blur-3xl pointer-events-none" />

          {/* Header - Fixed */}
          <div className="sticky top-0 bg-[#181a22] px-5 sm:px-6 py-4 flex justify-between items-center border-b border-white/10 z-10 flex-shrink-0">
            <div className="flex-1 min-w-0 pr-3">
              <div className="flex items-center gap-1.5 text-[#bd9f67] text-[10px] font-bold tracking-wider uppercase mb-0.5">
              Artist Ministry Network
              </div>
              <h2 className="text-lg sm:text-2xl font-bold tracking-tight text-white truncate">
                Artist Account Registration
              </h2>
              <p className="text-white/50 text-xs mt-0.5">
                {step === 'info' ? 'Step 1: Complete your profile information' : 'Step 2: Choose your subscription tier'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/40 hover:text-white hover:bg-white/10 p-2 rounded-full transition flex-shrink-0"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="overflow-y-auto p-5 sm:p-6 space-y-6 flex-1">
            <AnimatePresence mode="wait">
              {/* STEP 1: Artist Information Form */}
              {step === 'info' && (
                <motion.div
                  key="info"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-5"
                >
                  {/* Alert Info Box */}
                  <div className="bg-[#181a22] border border-white/10 rounded-xl p-3.5 sm:p-4 flex gap-3 items-start">
                    <AlertCircle className="text-[#bd9f67] flex-shrink-0 mt-0.5" size={18} />
                    <div className="text-xs text-white/70 leading-relaxed">
                      <p className="font-semibold text-white mb-0.5">Profile Guidelines</p>
                      <p>Provide detailed artist information. This profile will be displayed across your published songs and media in the platform.</p>
                    </div>
                  </div>

                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[#bd9f67]">Basic Details</h3>
                    
                    {/* Artist/Band Name */}
                    <div>
                      <label className="block text-xs font-semibold text-white/80 mb-1.5">
                        Artist / Band Name *
                      </label>
                      <input
                        type="text"
                        value={artistInfo.name}
                        onChange={(e) => setArtistInfo({ ...artistInfo, name: e.target.value })}
                        placeholder="e.g., The Asidors, John Doe"
                        className={`w-full px-3.5 py-2.5 bg-black/40 border rounded-xl text-xs sm:text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition ${
                          formErrors.name ? 'border-red-500/80' : 'border-white/10'
                        }`}
                      />
                      {formErrors.name && <p className="text-red-400 text-xs mt-1">{formErrors.name}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-semibold text-white/80 mb-1.5">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={artistInfo.email}
                        onChange={(e) => setArtistInfo({ ...artistInfo, email: e.target.value })}
                        placeholder="your@email.com"
                        className={`w-full px-3.5 py-2.5 bg-black/40 border rounded-xl text-xs sm:text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition ${
                          formErrors.email ? 'border-red-500/80' : 'border-white/10'
                        }`}
                      />
                      {formErrors.email && <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>}
                    </div>

                    {/* Artist Type and Genre */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-white/80 mb-1.5">
                          Type *
                        </label>
                        <select
                          value={artistInfo.type}
                          onChange={(e) => setArtistInfo({ ...artistInfo, type: e.target.value })}
                          className={`w-full px-3.5 py-2.5 bg-[#121318] border rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:border-[#bd9f67] transition ${
                            formErrors.type ? 'border-red-500/80' : 'border-white/10'
                          }`}
                        >
                          <option value="" className="bg-[#121318]">Select type</option>
                          <option value="solo" className="bg-[#121318]">Solo Artist</option>
                          <option value="band" className="bg-[#121318]">Band / Group</option>
                        </select>
                        {formErrors.type && <p className="text-red-400 text-xs mt-1">{formErrors.type}</p>}
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-white/80 mb-1.5">
                          Primary Genre *
                        </label>
                        <input
                          type="text"
                          value={artistInfo.genre}
                          onChange={(e) => setArtistInfo({ ...artistInfo, genre: e.target.value })}
                          placeholder="e.g., Gospel, Contemporary Worship"
                          className={`w-full px-3.5 py-2.5 bg-black/40 border rounded-xl text-xs sm:text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition ${
                            formErrors.genre ? 'border-red-500/80' : 'border-white/10'
                          }`}
                        />
                        {formErrors.genre && <p className="text-red-400 text-xs mt-1">{formErrors.genre}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Biography */}
                  <div className="space-y-4 pt-2">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[#bd9f67]">About the Artist</h3>
                    
                    <div>
                      <label className="block text-xs font-semibold text-white/80 mb-1.5">
                        Bio / Ministry Description
                      </label>
                      <textarea
                        value={artistInfo.bio}
                        onChange={(e) => setArtistInfo({ ...artistInfo, bio: e.target.value.slice(0, 500) })}
                        placeholder="Tell listeners about your background, vision, and music..."
                        rows="3"
                        className="w-full px-3.5 py-2.5 bg-black/40 border border-white/10 rounded-xl text-xs sm:text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition"
                      />
                      <p className="text-[10px] text-white/40 mt-1 text-right">{artistInfo.bio.length}/500 characters</p>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-white/80 mb-1.5">
                        Official Website (Optional)
                      </label>
                      <input
                        type="url"
                        value={artistInfo.website}
                        onChange={(e) => setArtistInfo({ ...artistInfo, website: e.target.value })}
                        placeholder="https://yourwebsite.com"
                        className="w-full px-3.5 py-2.5 bg-black/40 border border-white/10 rounded-xl text-xs sm:text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition"
                      />
                    </div>
                  </div>

                  {/* Social Media */}
                  <div className="space-y-4 pt-2">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[#bd9f67]">Social Handles (Optional)</h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {['instagram', 'twitter', 'youtube', 'facebook'].map((platform) => (
                        <div key={platform}>
                          <label className="block text-xs font-semibold text-white/80 mb-1.5 capitalize">
                            {platform}
                          </label>
                          <input
                            type="text"
                            value={artistInfo.socialMedia[platform]}
                            onChange={(e) => setArtistInfo({
                              ...artistInfo,
                              socialMedia: { ...artistInfo.socialMedia, [platform]: e.target.value }
                            })}
                            placeholder={platform === 'youtube' ? 'Channel link or name' : `@username`}
                            className="w-full px-3.5 py-2.5 bg-black/40 border border-white/10 rounded-xl text-xs sm:text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-5 border-t border-white/10">
                    <button
                      onClick={onClose}
                      className="px-5 py-2.5 border border-white/10 bg-white/5 hover:bg-white/10 rounded-xl text-xs font-semibold text-white/70 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleNextStep}
                      className="px-6 py-2.5 bg-[#bd9f67] hover:bg-[#d0b07a] text-gray-950 font-bold rounded-xl text-xs sm:text-sm transition shadow-lg shadow-[#bd9f67]/20 flex items-center justify-center gap-2"
                    >
                      <span>Choose Plan</span>
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* STEP 2: Subscription Tier Selection */}
              {step === 'tier' && (
                <motion.div
                  key="tier"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Summary Bar */}
                  <div className="bg-[#181a22] border border-white/10 rounded-xl p-3.5 flex items-center justify-between">
                    <div className="min-w-0 pr-2">
                      <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">Registering As</p>
                      <p className="text-xs sm:text-sm font-bold text-white truncate">{artistInfo.name}</p>
                    </div>
                    <button
                      onClick={handleBack}
                      className="text-xs text-[#bd9f67] hover:underline flex items-center gap-1 flex-shrink-0"
                    >
                      <ChevronLeft size={14} /> Edit Info
                    </button>
                  </div>

                  {/* Pricing Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {tiers.map((tier) => {
                      const details = SUBSCRIPTION_DETAILS[tier];
                      const isSelected = selectedTier === tier;

                      return (
                        <motion.div
                          key={tier}
                          whileHover={{ y: -3 }}
                          onClick={() => setSelectedTier(tier)}
                          className={`relative rounded-2xl border p-5 cursor-pointer transition-all duration-300 flex flex-col justify-between ${
                            isSelected
                              ? 'border-[#bd9f67] bg-[#bd9f67]/10 shadow-xl shadow-[#bd9f67]/10'
                              : 'border-white/10 bg-[#181a22]/70 hover:border-white/20'
                          }`}
                        >
                          {/* Popular Badge */}
                          {tier === SUBSCRIPTION_TIERS.PRO && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                              <span className="bg-[#bd9f67] text-gray-950 text-[9px] font-extrabold px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-md">
                                MOST POPULAR
                              </span>
                            </div>
                          )}

                          {/* Selected Check Indicator */}
                          {isSelected && (
                            <div className="absolute top-4 right-4 bg-[#bd9f67] text-gray-950 rounded-full p-1 shadow-md">
                              <Check size={14} strokeWidth={3} />
                            </div>
                          )}

                          <div>
                            {/* Icon & Name */}
                            <div className="text-2xl mb-2">{details.icon}</div>
                            <h3 className="text-base font-bold text-white mb-1">{details.name}</h3>
                            <p className="text-[11px] text-white/50 mb-4 leading-relaxed">{details.description}</p>

                            {/* Price */}
                            <div className="mb-5 pb-4 border-b border-white/5">
                              <p className="text-2xl sm:text-3xl font-extrabold text-[#bd9f67]">
                                ${details.price.toFixed(2)}
                              </p>
                              <p className="text-[11px] text-white/40">per {details.billingCycle}</p>
                            </div>

                            {/* Features */}
                            <div className="space-y-2.5 mb-6">
                              {details.features.map((feature, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-xs">
                                  <Check
                                    size={14}
                                    className="text-[#bd9f67] flex-shrink-0 mt-0.5"
                                  />
                                  <div className="text-white/80 text-[11px]">
                                    <span className="font-medium">{feature.name}</span>
                                    {feature.unlimited && (
                                      <span className="text-[#bd9f67] font-semibold"> - Unlimited</span>
                                    )}
                                    {feature.limit && !feature.unlimited && (
                                      <span className="text-white/40"> - {feature.limit}</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Select Button */}
                          <button
                            className={`w-full py-2.5 rounded-xl font-bold text-xs transition duration-200 ${
                              isSelected
                                ? 'bg-[#bd9f67] text-gray-950'
                                : 'bg-white/5 text-white/80 hover:bg-white/10 border border-white/10'
                            }`}
                          >
                            {isSelected ? 'Selected' : 'Select Plan'}
                          </button>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Terms Agreement */}
                  <div className="bg-black/30 border border-white/5 rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="w-4 h-4 mt-0.5 rounded border-white/20 bg-black/50 text-[#bd9f67] focus:ring-0 flex-shrink-0"
                      />
                      <p className="text-xs text-white/60 leading-relaxed">
                        I agree that all uploaded music and video submissions undergo review by The Asidors admin team to maintain quality and community guidelines prior to public release.
                      </p>
                    </label>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-4 border-t border-white/10">
                    <button
                      onClick={handleBack}
                      className="px-5 py-2.5 border border-white/10 bg-white/5 hover:bg-white/10 rounded-xl text-xs font-semibold text-white/70 transition disabled:opacity-50"
                      disabled={isProcessing}
                    >
                      Back
                    </button>
                    <button
                      onClick={() => handleSelectAndProceed(selectedTier)}
                      disabled={!selectedTier || !agreedToTerms || isProcessing}
                      className="px-6 py-2.5 bg-[#bd9f67] hover:bg-[#d0b07a] text-gray-950 font-bold rounded-xl text-xs sm:text-sm disabled:opacity-40 disabled:cursor-not-allowed transition shadow-lg shadow-[#bd9f67]/20 flex items-center justify-center gap-2"
                    >
                      <Zap size={16} />
                      <span>{isProcessing ? 'Processing...' : 'Continue to Payment'}</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}